import { diffWords } from 'diff';
import { CalificacionBadge } from '@/components/revision/CalificacionBadge';
import { RevisionFieldModal } from '@/components/revision/RevisionFieldModal';
import { TernaFieldRevision, isTernaFieldPendiente } from '@/components/revision/TernaFieldRevision';
import { ShiftActionOverlay } from '@/components/revision/ShiftActionOverlay';
import { buildHistorialFieldMap, useLatestHistorialMap, type HistorialFullEntry, type HistorialMap } from '@/hooks/useLatestHistorialMap';
import type { EvaluacionAdminResponse, EvaluacionRequisitoAdmin, EvaluacionTernaAdmin, RequisitoAdmin } from '@/types/evaluacionAdmin';
import { renderWithBold } from '@/utils/renderWithBold';
import { historialKey } from '@/utils/revisionHistorial';
import { AlertTriangle, Check, Clock, GitCompare, RotateCw, XCircle } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

// ============================
// Types & Helpers (Grid Engine)
// ============================

type CalificacionType = 'C' | 'NC' | 'CP' | 'NA';

function getActivaFecha(val: unknown): string | null {
  if (!val) return null;
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val !== null && 'activa' in val) return (val as { activa: string | null }).activa ?? null;
  return null;
}
function getAnteriorFecha(val: unknown): string | null {
  if (!val || typeof val === 'string') return null;
  if (typeof val === 'object' && val !== null && 'anterior' in val) return (val as { anterior: string | null }).anterior ?? null;
  return null;
}
function parseDateStr(s: string | null | undefined): string {
  if (!s) return '';
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleDateString('es-CO');
  } catch {
    return s;
  }
}

interface FilaEvaluacionRO {
  id: string;
  ternaId: number | null;
  grupo: string | null;
  celdas: Record<number, RequisitoAdmin | null>;
}

interface GrupoEvaluacionRO {
  id: string;
  filas: FilaEvaluacionRO[];
}

function buildGruposDesdeAdmin(data: EvaluacionAdminResponse): GrupoEvaluacionRO[] {
  const columnas = data.combinacion?.ntcs ?? data.ntcs.map((n) => ({ ntcId: n.ntcId, nombre: n.nombre, orden: n.orden || 1 }));
  const reqMap = new Map<string, RequisitoAdmin>();
  data.requisitos.forEach((r) => reqMap.set(r.requisitoId, r));

  const filasPorOrden = new Map<number, FilaEvaluacionRO[]>();

  for (const terna of data.ternas) {
    const celdas: Record<number, RequisitoAdmin | null> = {};
    if (terna.requisitoC1Id) celdas[1] = reqMap.get(terna.requisitoC1Id) || null;
    if (terna.requisitoC2Id) celdas[2] = reqMap.get(terna.requisitoC2Id) || null;
    if (terna.requisitoC3Id) celdas[3] = reqMap.get(terna.requisitoC3Id) || null;

    const fila: FilaEvaluacionRO = {
      id: terna.id.toString(),
      ternaId: terna.id,
      grupo: terna.grupo,
      celdas,
    };

    const ordenVal = terna.orden || 0;
    if (!filasPorOrden.has(ordenVal)) filasPorOrden.set(ordenVal, []);
    filasPorOrden.get(ordenVal)!.push(fila);
  }

  return Array.from(filasPorOrden.entries())
    .sort(([a], [b]) => a - b)
    .map(([orden, filas]) => ({
      id: `grupo-${orden}`,
      filas: filas.sort((a, b) => {
        if (a.grupo && !b.grupo) return -1;
        if (!a.grupo && b.grupo) return 1;
        if (a.grupo && b.grupo && a.grupo !== b.grupo) return a.grupo.localeCompare(b.grupo);
        return (a.ternaId ?? Number(a.id)) - (b.ternaId ?? Number(b.id));
      }),
    }));
}

function calcularSpansRO(filas: FilaEvaluacionRO[], ordenes: number[]) {
  const spans: Record<number, { startRow: number; span: number; req: RequisitoAdmin | null }[]> = {};
  for (const orden of ordenes) {
    spans[orden] = [];
    let i = 0;
    while (i < filas.length) {
      const req = filas[i].celdas[orden] ?? null;
      let j = i + 1;
      while (j < filas.length && req !== null && filas[j].celdas[orden]?.requisitoId === req.requisitoId) j++;
      spans[orden].push({ startRow: i, span: j - i, req });
      i = j;
    }
  }
  return spans;
}

function calcularEvalSpansRO(filas: FilaEvaluacionRO[]) {
  const spans: { startRow: number; span: number; evalKey: string | null }[] = [];
  let i = 0;
  while (i < filas.length) {
    const fila = filas[i];
    if (fila.ternaId == null) {
      spans.push({ startRow: i, span: 1, evalKey: null });
      i++;
    } else if (fila.grupo) {
      const evalKey = `grupo:${fila.grupo}`;
      let j = i + 1;
      while (j < filas.length && filas[j].grupo === fila.grupo) j++;
      spans.push({ startRow: i, span: j - i, evalKey });
      i = j;
    } else {
      spans.push({ startRow: i, span: 1, evalKey: `terna:${fila.ternaId}` });
      i++;
    }
  }
  return spans;
}

/**
 * Calcula, para cada fila (eval-span), los requisitos y campos de terna que aún no tienen
 * veredicto de revisión.
 *
 * Un requisito cuenta como pendiente si su `estadoRevision` es `null`/`undefined` o `'PENDIENTE'`,
 * SIN IMPORTAR si su calificación cambió respecto a la asesoría anterior (no se filtra por
 * `diffReqMap`). Esto es intencional: el backend (`puedeAprobarseSalvoCadena`) bloquea la
 * aprobación de la asesoría por cualquier `EvaluacionRequisito` sin revisar, tenga o no diff de
 * calificación. Si este conteo solo mirara el diff, la matriz podría mostrar "0 pendientes"
 * mientras el backend igual rechaza la aprobación.
 *
 * Un campo de terna cuenta si `diffTernaMap` tiene ese campo y su último historial sigue
 * pendiente (mismo criterio que `TernaFieldRevision`/`isTernaFieldPendiente`).
 */
function computePendingByEvalSpan(params: {
  evalSpans: { startRow: number; span: number; evalKey: string | null }[];
  spans: Record<number, { startRow: number; span: number; req: RequisitoAdmin | null }[]>;
  ordenes: number[];
  evalTernaMap: Map<string, EvaluacionTernaAdmin>;
  diffTernaMap: Map<string, any>;
  diffReqMap?: Map<string, any>;
  calMap: Map<string, EvaluacionRequisitoAdmin>;
  latestHistorialMap: HistorialMap;
}): PendingItems[] {
  const { evalSpans, spans, ordenes, evalTernaMap, diffTernaMap, diffReqMap, calMap, latestHistorialMap } = params;

  const TERNA_CAMPOS: { campo: 'observaciones' | 'planMejora' | 'fechaImplementacion'; tipoHist: string }[] = [
    { campo: 'observaciones', tipoHist: 'OBSERVACIONES' },
    { campo: 'planMejora', tipoHist: 'PLANMEJORA' },
    { campo: 'fechaImplementacion', tipoHist: 'FECHAIMPLEMENTACION' },
  ];

  return evalSpans.map((item) => {
    const pending: PendingItems = { requisitos: [], camposTerna: [] };
    const evalTerna = item.evalKey ? evalTernaMap.get(item.evalKey) : null;
    const cambiosTerna = item.evalKey ? diffTernaMap.get(item.evalKey) : null;

    if (evalTerna && cambiosTerna) {
      for (const { campo, tipoHist } of TERNA_CAMPOS) {
        if (cambiosTerna[campo] === undefined) continue;
        const h = latestHistorialMap.get(historialKey(tipoHist, evalTerna.id));
        if (isTernaFieldPendiente(h?.estado)) {
          pending.camposTerna.push({ evalTernaId: evalTerna.id, campo });
        }
      }
    }

    const rowEnd = item.startRow + item.span;
    const vistos = new Set<number>();
    for (const orden of ordenes) {
      for (const s of spans[orden] ?? []) {
        if (s.req === null) continue;
        if (!(s.startRow < rowEnd && s.startRow + s.span > item.startRow)) continue;
        const calObj = calMap.get(s.req.requisitoId);
        if (!calObj?.evaluacionRequisito) continue;

        const estadoRevision = calObj.evaluacionRequisito.estadoRevision;

        // Si diffReqMap tiene datos y el requisito no tuvo cambios en esta ronda (y no está rechazado), no cuenta como pendiente
        if (diffReqMap && diffReqMap.size > 0 && !diffReqMap.has(s.req.requisitoId) && estadoRevision !== 'RECHAZADO') {
          continue;
        }

        const estaPendiente = !estadoRevision || estadoRevision === 'PENDIENTE';
        const evalId = calObj.evaluacionRequisito.id;
        if (estaPendiente && evalId && !vistos.has(evalId)) {
          vistos.add(evalId);
          pending.requisitos.push(evalId);
        }
      }
    }

    return pending;
  });
}

// ============================
// Review modal target
// ============================

type PendingItems = {
  requisitos: number[];
  camposTerna: { evalTernaId: number; campo: 'observaciones' | 'planMejora' | 'fechaImplementacion' }[];
};

interface ReviewModalState {
  kind: 'terna' | 'requisito';
  titulo: string;
  textoActual?: string | null;
  textoAnterior?: string | null;
  /** Numeral y capítulo del requisito — solo aplica a kind="requisito". */
  numeral?: string;
  capitulo?: string;
  /** Calificación vigente / anterior del requisito (C/NC/CP/NA), para el badge con flecha. */
  calActual?: CalificacionType | null;
  calAnterior?: CalificacionType | null;
  historialList?: HistorialFullEntry[];
  estadoActual?: 'APROBADO' | 'RECHAZADO' | 'COMENTADO';
  observacionActual?: string;
  onAprobar?: (observacion?: string) => Promise<void>;
  onRechazar?: (observacion?: string) => Promise<void>;
}

/**
 * El último veredicto del admin para este elemento, si el turno sigue abierto (nadie respondió
 * desde entonces). Sirve para prellenar el textarea con el mensaje ya escrito en vez de arrancar
 * vacío.
 */
function ultimoVeredictoAbierto(
  entries: HistorialFullEntry[] | undefined
): { estado: 'APROBADO' | 'RECHAZADO' | 'COMENTADO'; observacion?: string } | undefined {
  if (!entries || entries.length === 0) return undefined;
  const ordenados = [...entries].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  const ultimo = ordenados[ordenados.length - 1];
  if (ultimo.estado === 'APROBADO' || ultimo.estado === 'RECHAZADO' || ultimo.estado === 'COMENTADO') {
    return { estado: ultimo.estado, observacion: ultimo.observacion ?? undefined };
  }
  return undefined;
}

// ============================
// Sub-components
// ============================

// ============================
// Saved comments (relocated to the "Comentarios" column)
// ============================

interface SavedCommentGroupData {
  key: string;
  label: string;
  subtitle?: string;
  appeal: HistorialFullEntry | null;
  admin: HistorialFullEntry | null;
}

function extractSavedComments(entries: HistorialFullEntry[] | undefined): {
  appeal: HistorialFullEntry | null;
  admin: HistorialFullEntry | null;
} {
  const list = entries ?? [];
  const appeal = list.find((e) => e.estado === 'RESPUESTA_ASESOR') ?? null;
  const admin = list.find((e) => e.estado === 'APROBADO' || e.estado === 'RECHAZADO' || e.estado === 'COMENTADO') ?? null;
  return { appeal, admin };
}

function SavedCommentGroup({
  label,
  subtitle,
  appeal,
  admin,
}: {
  label: string;
  subtitle?: string;
  appeal: HistorialFullEntry | null;
  admin: HistorialFullEntry | null;
}) {
  const items: { fecha: string; element: React.ReactNode }[] = [];

  if (appeal) {
    items.push({
      fecha: appeal.fecha,
      element: (
        <div key="appeal" className="p-1.5 rounded bg-amber-50 border border-amber-200 text-[10px] text-amber-800 leading-tight">
          <span className="font-semibold">
            Respuesta del asesor (
            {new Date(appeal.fecha).toLocaleString('es-CO', {
              timeZone: 'UTC',
              hour12: true,
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
            ):
          </span>{' '}
          <span className="italic text-amber-950 whitespace-pre-wrap wrap-break-word">"{appeal.observacion}"</span>
        </div>
      ),
    });
  }

  if (admin) {
    const isAprobado = admin.estado === 'APROBADO';
    const isRechazado = admin.estado === 'RECHAZADO';
    items.push({
      fecha: admin.fecha,
      element: (
        <div key="admin">
          <span
            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold border ${
              isAprobado
                ? 'bg-[#eaf5ee] text-[#2d7a4f] border-[#b7dfc7]'
                : isRechazado
                  ? 'bg-[#fdf0ee] text-[#c0392b] border-[#f5c6c0]'
                  : 'bg-[#eff6ff] text-[#1d4ed8] border-[#bfdbfe]'
            }`}
          >
            {isAprobado ? 'Aprobado' : isRechazado ? 'Rechazado' : 'Comentado'}
          </span>
          {admin.observacion && (
            <span
              className={`text-[11px] block mt-0.5 italic whitespace-pre-wrap wrap-break-word ${
                isAprobado ? 'text-[#2d7a4f]' : isRechazado ? 'text-[#c0392b]' : 'text-[#1d4ed8]'
              }`}
            >
              "{admin.observacion}"
            </span>
          )}
        </div>
      ),
    });
  }

  if (items.length === 0) return null;

  items.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());

  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/40 p-2">
      <div className="text-[9px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">
        {label}
        {subtitle && <span className="block text-[8px] font-medium normal-case tracking-normal text-gray-400/70 mt-0.5">{subtitle}</span>}
      </div>
      <div className="space-y-1">{items.map((item) => item.element)}</div>
    </div>
  );
}

const RequisitoCell = React.memo<{
  req: RequisitoAdmin;
  cal: CalificacionType | null;
  prev?: CalificacionType | null;
  estadoRevision?: string | null;
  observacionRevision?: string | null;
  tieneEvaluacionRequisito?: boolean;
  isRevisionMode?: boolean;
  onOpenReview?: () => void;
  /** Shift+clic: revisa en bloque todas las filas que cubre el rowSpan de esta celda. */
  onBulkReview?: () => void;
  shiftHeld?: boolean;
  onQuickAprobar?: () => void;
  onQuickRechazar?: () => void;
}>(
  ({
    req,
    cal,
    prev,
    estadoRevision,
    observacionRevision,
    tieneEvaluacionRequisito,
    isRevisionMode,
    onOpenReview,
    onBulkReview,
    shiftHeld,
    onQuickAprobar,
    onQuickRechazar,
  }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const isClickable = !!onOpenReview;
    const estadoBg =
      isRevisionMode && tieneEvaluacionRequisito
        ? estadoRevision === 'APROBADO'
          ? 'bg-green-100'
          : estadoRevision === 'RECHAZADO'
            ? 'bg-red-100'
            : 'bg-yellow-100'
        : '';

    const handleActivate = () => {
      onOpenReview?.();
    };

    return (
      <ShiftActionOverlay onAprobar={onQuickAprobar} onRechazar={onQuickRechazar}>
        <div
          role={isClickable ? 'button' : undefined}
          tabIndex={isClickable ? 0 : undefined}
          className={`flex flex-col h-full min-h-[120px] justify-start relative rounded-lg -m-1 p-2.5 transition-colors ${
            isClickable ? `cursor-pointer hover:bg-blue-100/70 ${estadoBg}` : estadoBg
          }`}
          onClick={isClickable ? handleActivate : undefined}
          onKeyDown={
            isClickable
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleActivate();
                  }
                }
              : undefined
          }
        >
          <div className="font-black text-[12px] text-gray-900 mb-1">{req.numeral}</div>
          <div
            className={`text-[11px] text-gray-600 leading-relaxed wrap-break-word transition-all duration-300 ease-in-out ${
              isExpanded ? 'max-h-200 opacity-100' : 'max-h-16 line-clamp-3'
            }`}
          >
            {renderWithBold(req.descripcion)}
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="text-[10px] text-blue-600 font-bold mt-1 self-start hover:text-blue-800 transition-colors"
          >
            {isExpanded ? 'Ver menos' : 'Ver más'}
          </button>

          <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
            <CalificacionBadge cal={cal} prev={prev} />
          </div>

          {isRevisionMode && tieneEvaluacionRequisito && (
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {estadoRevision === 'APROBADO' && (
                <span className="px-2 py-0.5 rounded text-[10px] font-bold border shadow-2xs bg-[#eaf5ee] text-[#2d7a4f] border-[#b7dfc7] inline-flex items-center gap-1">
                  <Check className="w-3 h-3 text-[#2d7a4f]" /> Aprobado
                </span>
              )}
              {estadoRevision === 'RECHAZADO' && (
                <span
                  className="px-2 py-0.5 rounded text-[10px] font-bold border shadow-2xs bg-[#fdf0ee] text-[#c0392b] border-[#f5c6c0] inline-flex items-center gap-1"
                  title={observacionRevision ? `Observación: ${observacionRevision}` : undefined}
                >
                  <XCircle className="w-3 h-3 text-[#c0392b]" /> Rechazado
                </span>
              )}
              {(!estadoRevision || estadoRevision === 'PENDIENTE') && (
                <span className="px-2 py-0.5 rounded text-[10px] font-bold border shadow-2xs bg-[#fef9c3] text-[#854d0e] border-[#fef08a] inline-flex items-center gap-1">
                  <Clock className="w-3 h-3 text-[#854d0e]" /> Pendiente
                </span>
              )}
            </div>
          )}
        </div>
      </ShiftActionOverlay>
    );
  }
);

function WordDiffInline({ prev, current }: { prev: string; current: string }) {
  const parts = diffWords(prev, current);
  return (
    <p className="text-[11.5px] leading-relaxed wrap-break-word whitespace-pre-wrap font-medium">
      {parts.map((part, i) =>
        part.removed ? (
          <span key={i} className="bg-red-100 text-red-700 line-through decoration-red-400 rounded-sm px-0.5">
            {part.value}
          </span>
        ) : part.added ? (
          <span key={i} className="bg-green-100 text-green-800 font-bold rounded-sm px-0.5">
            {part.value}
          </span>
        ) : (
          <span key={i} className="text-gray-700">
            {part.value}
          </span>
        )
      )}
    </p>
  );
}

function DiffText({
  current,
  prev,
  label,
  inDiffContext = false,
  wordDiff = false,
}: {
  current?: string | null;
  prev?: string | null;
  label: string;
  inDiffContext?: boolean;
  wordDiff?: boolean;
}) {
  const hasValue = !!current;
  const hasPrev = inDiffContext && prev !== undefined && prev !== null && prev !== '';
  const isNew = inDiffContext && hasValue && !hasPrev;
  const hasDiff = inDiffContext && hasPrev && prev !== current;
  if (!current && !prev) return null;

  return (
    <div
      className={`mb-3.5 last:mb-0 transition-all ${
        hasDiff ? 'p-3 bg-blue-50/30 rounded-xl border border-blue-100/40 shadow-xs' : ''
      } ${isNew ? 'p-3 bg-green-50/40 rounded-xl border border-green-100/50 shadow-xs' : ''}`}
    >
      <div
        className={`text-[9px] font-bold uppercase tracking-widest mb-1.5 flex items-center gap-2 ${
          hasDiff ? 'text-blue-600' : isNew ? 'text-green-600' : 'text-gray-400'
        }`}
      >
        {label}
        {hasDiff && <span className="w-1 h-1 rounded-full bg-blue-400 animate-pulse" />}
        {isNew && (
          <span className="flex items-center gap-0.5 px-1 py-0.5 rounded bg-green-100 text-green-700 text-[8px] font-black leading-none">
            + nuevo
          </span>
        )}
      </div>
      <div
        className={`text-[11.5px] leading-relaxed wrap-break-word ${hasDiff ? 'text-blue-800' : isNew ? 'text-green-900' : 'text-gray-700'}`}
      >
        {hasDiff && wordDiff && prev && current ? (
          <WordDiffInline prev={prev} current={current} />
        ) : (
          <>
            {hasDiff && prev && (
              <div className="mb-2">
                <span className="text-blue-300 line-through opacity-80 italic decoration-blue-200 block mb-0.5 wrap-break-word whitespace-pre-wrap">
                  {prev}
                </span>
                <div className="h-px w-4 bg-blue-100 my-1" />
              </div>
            )}
            <p className={`wrap-break-word whitespace-pre-wrap ${hasDiff || isNew ? 'font-bold' : 'font-medium text-gray-600'}`}>
              {current || <span className="text-gray-300 italic">No registrado</span>}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ============================
// Main Component
// ============================

interface MatrizInlineProps {
  data: EvaluacionAdminResponse;
  className?: string;
  onRefresh?: () => void;
  isRevisionMode?: boolean;
  mode?: 'normal' | 'focus';
  /** Guarda de inmediato (sin batch): cada Aprobar/Rechazar en el modal persiste al toque. */
  onTernaFieldVeredicto?: (
    evalTernaId: number,
    campo: 'observaciones' | 'planMejora' | 'fechaImplementacion',
    estado: 'APROBADO' | 'RECHAZADO' | 'COMENTADO',
    observacion?: string
  ) => Promise<void>;
  onRequisitoVeredicto?: (evaluacionRequisitoId: number, estado: 'APROBADO' | 'RECHAZADO', observacion?: string) => Promise<void>;
  historialRevision?: any[];
}

export function MatrizInline({
  data,
  className = '',
  onRefresh,
  isRevisionMode = false,
  mode: modeProp,
  onTernaFieldVeredicto,
  onRequisitoVeredicto,
  historialRevision,
}: MatrizInlineProps) {
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [reviewModal, setReviewModal] = useState<ReviewModalState | null>(null);
  const [shiftHeld, setShiftHeld] = useState(false);
  const [bulkReview, setBulkReview] = useState<{ items: PendingItems; observacion: string } | null>(null);
  const [bulkSubmitting, setBulkSubmitting] = useState<'APROBADO' | 'RECHAZADO' | null>(null);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setShiftHeld(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setShiftHeld(false);
    };
    const onBlur = () => setShiftHeld(false);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // mode: 'focus' filters to ternas with changes; 'normal' shows all.
  // Default: 'focus' when in revision mode AND there are diffs to inspect, otherwise 'normal'.
  const initialHasDiffs = Boolean(
    data.diffActiva?.requisitos?.length ||
    (Array.isArray(data.diffActiva?.ternas) ? data.diffActiva.ternas.length > 0 : Object.keys(data.diffActiva?.ternas ?? {}).length > 0)
  );
  const defaultMode = isRevisionMode && initialHasDiffs ? 'focus' : 'normal';
  const [mode, setMode] = useState<'normal' | 'focus'>(modeProp ?? defaultMode);
  const showOnlyChanges = mode === 'focus';
  const [expandAllTexts, setExpandAllTexts] = useState(false);
  const [wordDiffAll, setWordDiffAll] = useState(false);

  const latestHistorialMap = useLatestHistorialMap(historialRevision as any);
  const historialFieldMap = useMemo(() => buildHistorialFieldMap((historialRevision ?? []) as any), [historialRevision]);

  const openTernaReview = (
    evalTernaId: number,
    campo: 'observaciones' | 'planMejora' | 'fechaImplementacion',
    label: string,
    tipoHist: 'OBSERVACIONES' | 'PLANMEJORA' | 'FECHAIMPLEMENTACION',
    textoActual?: string | null,
    textoAnterior?: string | null
  ) => {
    const historialElemento = historialFieldMap.get(historialKey(tipoHist, evalTernaId));
    const current = ultimoVeredictoAbierto(historialElemento);
    setReviewModal({
      kind: 'terna',
      titulo: label,
      textoActual,
      textoAnterior,
      historialList: historialElemento,
      estadoActual: current?.estado,
      observacionActual: current?.observacion,
      onAprobar: async (observacion) => onTernaFieldVeredicto?.(evalTernaId, campo, 'APROBADO', observacion),
      onRechazar: async (observacion) => onTernaFieldVeredicto?.(evalTernaId, campo, 'RECHAZADO', observacion),
    });
  };

  const openRequisitoReview = (
    evalId: number,
    numeral: string,
    capitulo: string,
    descripcion: string,
    cal: CalificacionType | null,
    prev: CalificacionType | null
  ) => {
    const historialElemento = historialFieldMap.get(historialKey('REQUISITO', evalId));
    const current = ultimoVeredictoAbierto(historialElemento);
    setReviewModal({
      kind: 'requisito',
      titulo: `Requisito ${numeral}`,
      textoActual: descripcion,
      textoAnterior: null,
      numeral,
      capitulo,
      calActual: cal,
      calAnterior: prev,
      historialList: historialElemento,
      estadoActual: current?.estado,
      observacionActual: current?.observacion,
      onAprobar: async (observacion) => onRequisitoVeredicto?.(evalId, 'APROBADO', observacion),
      onRechazar: async (observacion) => onRequisitoVeredicto?.(evalId, 'RECHAZADO', observacion),
    });
  };

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
      setLastUpdated(new Date());
    }
  };

  const columnas = useMemo(() => {
    const base = data.combinacion?.ntcs ?? data.ntcs.map((n) => ({ ntcId: n.ntcId, nombre: n.nombre, orden: n.orden || 1 }));
    return [...base].sort((a, b) => a.orden - b.orden);
  }, [data.combinacion, data.ntcs]);

  const ordenes = useMemo(() => columnas.map((c) => c.orden), [columnas]);
  const grupos = useMemo(() => buildGruposDesdeAdmin(data), [data]);

  const flatFilas = useMemo(() => {
    const rows: { fila: FilaEvaluacionRO; isLastInGrupo: boolean }[] = [];
    grupos.forEach((g) => {
      g.filas.forEach((f, i) => {
        rows.push({ fila: f, isLastInGrupo: i === g.filas.length - 1 });
      });
    });
    return rows;
  }, [grupos]);

  const allFilas = useMemo(() => flatFilas.map((r) => r.fila), [flatFilas]);
  const globalSpans = useMemo(() => calcularSpansRO(allFilas, ordenes), [allFilas, ordenes]);
  const globalEvalSpans = useMemo(() => calcularEvalSpansRO(allFilas), [allFilas]);

  // Lookup maps
  const calMap = useMemo(() => {
    const m = new Map<string, EvaluacionRequisitoAdmin>();
    data.evaluacion.forEach((e: EvaluacionRequisitoAdmin) => m.set(e.requisitoId, e));
    return m;
  }, [data.evaluacion]);

  const evalTernaMap = useMemo(() => {
    const m = new Map<string, EvaluacionTernaAdmin>();
    data.evaluacionesTernas.forEach((et) => {
      const key = et.ternaId ? (et.grupo ? `grupo:${et.grupo}` : `terna:${et.ternaId}`) : et.grupo ? `grupo:${et.grupo}` : null;
      if (key) m.set(key, et);
    });
    return m;
  }, [data.evaluacionesTernas]);

  // Diff maps
  const diffReqMap = useMemo(() => {
    const m = new Map<string, { prev?: CalificacionType | null; activa: CalificacionType }>();
    if (Array.isArray(data.diffActiva?.requisitos)) {
      data.diffActiva!.requisitos.forEach((d) => {
        const activa = d.calificacion || d.calificacionActiva;
        if (d.requisitoId && activa) {
          m.set(d.requisitoId, {
            prev: d.calificacionAnterior ?? undefined,
            activa,
          });
        }
      });
    }
    return m;
  }, [data.diffActiva]);

  const diffTernaMap = useMemo(() => {
    const m = new Map<string, any>();
    const diffTernas = data.diffActiva?.ternas;
    if (!diffTernas) return m;

    if (Array.isArray(diffTernas)) {
      diffTernas.forEach((d: any) => {
        const cambios = d.cambios || d;
        if (d.ternaId) m.set(`terna:${d.ternaId}`, cambios);
        if (d.grupo) m.set(`grupo:${d.grupo}`, cambios);
      });
    } else {
      Object.entries(diffTernas).forEach(([id, val]: [string, any]) => {
        const key = !isNaN(Number(id)) ? `terna:${id}` : `grupo:${id}`;
        // Normalize: new backend sends flat values instead of {activa, anterior}
        const cambios: any = {};

        if (typeof val.observaciones === 'string') {
          cambios.observaciones = { activa: val.observaciones };
        } else if (val.observaciones) {
          cambios.observaciones = val.observaciones;
        }

        if (typeof val.planMejora === 'string') {
          cambios.planMejora = { activa: val.planMejora };
        } else if (val.planMejora) {
          cambios.planMejora = val.planMejora;
        }

        if (val.fechaImplementacion) cambios.fechaImplementacion = val.fechaImplementacion;
        if (val.evidenciaIds) cambios.evidenciaIds = val.evidenciaIds;

        m.set(key, cambios);
      });
    }
    return m;
  }, [data.diffActiva]);

  const hasDiffs = diffReqMap.size > 0 || diffTernaMap.size > 0;

  useEffect(() => {
    if (modeProp !== undefined) {
      setMode(modeProp);
    } else if (!hasDiffs && mode === 'focus') {
      setMode('normal');
    }
  }, [modeProp, hasDiffs, mode]);

  const hasAnyTextDiff = useMemo(() => {
    for (const [, cambios] of diffTernaMap) {
      for (const campo of ['observaciones', 'planMejora', 'fechaImplementacion'] as const) {
        const f = (cambios as any)[campo];
        if (f && typeof f === 'object' && f.anterior != null && f.activa != null && f.anterior !== f.activa) return true;
      }
    }
    return false;
  }, [diffTernaMap]);

  // When showOnlyChanges: filter to eval-span groups that have at least one diff,
  // then recompute spans on the filtered row array.
  const activeFilas = useMemo(() => {
    if (!showOnlyChanges) return allFilas;

    const evalSpans = calcularEvalSpansRO(allFilas);
    const changedRows = new Set<number>();

    for (const span of evalSpans) {
      const hasEvalDiff = span.evalKey !== null && diffTernaMap.has(span.evalKey);
      const hasReqDiff = ordenes.some((orden) =>
        globalSpans[orden]?.some(
          (s) =>
            s.startRow < span.startRow + span.span &&
            s.startRow + s.span > span.startRow &&
            s.req !== null &&
            diffReqMap.has(s.req.requisitoId)
        )
      );
      if (hasEvalDiff || hasReqDiff) {
        for (let k = 0; k < span.span; k++) changedRows.add(span.startRow + k);
      }
    }

    return allFilas.filter((_, i) => changedRows.has(i));
  }, [allFilas, showOnlyChanges, diffReqMap, diffTernaMap, ordenes, globalSpans]);

  const activeSpans = useMemo(() => calcularSpansRO(activeFilas, ordenes), [activeFilas, ordenes]);
  const activeEvalSpans = useMemo(() => calcularEvalSpansRO(activeFilas), [activeFilas]);

  const activeFlatFilas = useMemo(() => {
    const evalSpans = calcularEvalSpansRO(activeFilas);
    const lastRowInGroup = new Set(evalSpans.map((s) => s.startRow + s.span - 1));
    return activeFilas.map((fila, i) => ({ fila, isLastInGrupo: lastRowInGroup.has(i) }));
  }, [activeFilas]);

  // Saved comments aggregated per eval-span row (row-aligned with the Evaluación column).
  const commentsByEvalSpan = useMemo(() => {
    return activeEvalSpans.map((item) => {
      const groups: SavedCommentGroupData[] = [];
      const evalTerna = item.evalKey ? evalTernaMap.get(item.evalKey) : null;

      if (evalTerna) {
        const ternaFields: { tipo: string; label: string }[] = [
          { tipo: 'OBSERVACIONES', label: 'Observaciones' },
          { tipo: 'PLANMEJORA', label: 'Plan de Mejora' },
          { tipo: 'FECHAIMPLEMENTACION', label: 'Fecha Implementación' },
        ];
        for (const f of ternaFields) {
          const { appeal, admin } = extractSavedComments(historialFieldMap.get(historialKey(f.tipo, evalTerna.id)));
          if (appeal || admin) groups.push({ key: `terna:${evalTerna.id}:${f.tipo}`, label: f.label, appeal, admin });
        }
      }

      // Requisitos whose span overlaps this eval-span row range.
      const rowEnd = item.startRow + item.span;
      for (const orden of ordenes) {
        for (const s of activeSpans[orden] ?? []) {
          if (s.req === null) continue;
          if (s.startRow < rowEnd && s.startRow + s.span > item.startRow) {
            const evalId = calMap.get(s.req.requisitoId)?.evaluacionRequisito?.id;
            if (!evalId) continue;
            const { appeal, admin } = extractSavedComments(historialFieldMap.get(historialKey('REQUISITO', evalId)));
            if (appeal || admin) {
              const ntcName = columnas.find((c) => c.ntcId === s.req!.ntcId)?.nombre;
              groups.push({ key: `req:${evalId}`, label: `Requisito ${s.req.numeral}`, subtitle: ntcName, appeal, admin });
            }
          }
        }
      }

      return groups;
    });
  }, [activeEvalSpans, activeSpans, columnas, ordenes, evalTernaMap, calMap, historialFieldMap]);

  const hasAnyComment = useMemo(() => commentsByEvalSpan.some((g) => g.length > 0), [commentsByEvalSpan]);

  // Items pendientes de veredicto por fila (eval-span) VISIBLE (respeta el modo focus), para los
  // botones "Aprobar fila" que se muestran junto a cada fila renderizada.
  const pendingByEvalSpan = useMemo<PendingItems[]>(
    () =>
      computePendingByEvalSpan({
        evalSpans: activeEvalSpans,
        spans: activeSpans,
        ordenes,
        evalTernaMap,
        diffTernaMap,
        diffReqMap,
        calMap,
        latestHistorialMap,
      }),
    [activeEvalSpans, activeSpans, ordenes, evalTernaMap, diffTernaMap, diffReqMap, calMap, latestHistorialMap]
  );

  // Items pendientes de veredicto en TODA la matriz (ignora el modo focus / "Solo cambios"). Sirve
  // de base para el conteo del banner y para "Aprobar todo".
  const pendingByEvalSpanGlobal = useMemo<PendingItems[]>(
    () =>
      computePendingByEvalSpan({
        evalSpans: globalEvalSpans,
        spans: globalSpans,
        ordenes,
        evalTernaMap,
        diffTernaMap,
        diffReqMap,
        calMap,
        latestHistorialMap,
      }),
    [globalEvalSpans, globalSpans, ordenes, evalTernaMap, diffTernaMap, diffReqMap, calMap, latestHistorialMap]
  );

  const totalPendientes = useMemo(() => {
    const reqSet = new Set<number>();
    const camposSet = new Set<string>();
    for (const p of pendingByEvalSpanGlobal) {
      for (const id of p.requisitos) reqSet.add(id);
      for (const c of p.camposTerna) camposSet.add(`${c.evalTernaId}:${c.campo}`);
    }
    return reqSet.size + camposSet.size;
  }, [pendingByEvalSpanGlobal]);

  const [aprobandoTodo, setAprobandoTodo] = useState(false);
  const [aprobandoFila, setAprobandoFila] = useState<number | null>(null);

  const aprobarItems = async (items: PendingItems) => {
    await Promise.all([
      ...items.requisitos.map((evalId) => onRequisitoVeredicto?.(evalId, 'APROBADO')),
      ...items.camposTerna.map(({ evalTernaId, campo }) => onTernaFieldVeredicto?.(evalTernaId, campo, 'APROBADO')),
    ]);
  };

  const handleAprobarTodo = async () => {
    setAprobandoTodo(true);
    try {
      // Usa la lista global (no la filtrada por modo focus) para garantizar que se resuelvan
      // TODOS los pendientes reales, incluso si el modo "Solo cambios" oculta alguna fila.
      for (const items of pendingByEvalSpanGlobal) {
        await aprobarItems(items);
      }
      onRefresh?.();
    } finally {
      setAprobandoTodo(false);
    }
  };

  const handleAprobarFila = async (rowIndex: number, items: PendingItems) => {
    setAprobandoFila(rowIndex);
    try {
      await aprobarItems(items);
      onRefresh?.();
    } finally {
      setAprobandoFila(null);
    }
  };

  // Todo lo pendiente (de cualquier columna) en las filas [startRow, startRow+span) — usado por
  // el shift+clic sobre una celda con rowSpan, para revisar en bloque el grupo de ternas que esa
  // celda cubre (no solo el requisito de esa columna, sino también las otras columnas y los campos
  // de terna de esas mismas filas).
  const pendingForRowRange = (startRow: number, span: number): PendingItems => {
    const rowEnd = startRow + span;
    const requisitosSet = new Set<number>();
    const camposSeen = new Set<string>();
    const camposTerna: PendingItems['camposTerna'] = [];
    activeEvalSpans.forEach((es, j) => {
      if (!(es.startRow < rowEnd && es.startRow + es.span > startRow)) return;
      const items = pendingByEvalSpan[j];
      if (!items) return;
      items.requisitos.forEach((id) => requisitosSet.add(id));
      items.camposTerna.forEach((c) => {
        const key = `${c.evalTernaId}:${c.campo}`;
        if (!camposSeen.has(key)) {
          camposSeen.add(key);
          camposTerna.push(c);
        }
      });
    });
    return { requisitos: Array.from(requisitosSet), camposTerna };
  };

  const handleBulkVeredicto = async (estado: 'APROBADO' | 'RECHAZADO') => {
    if (!bulkReview) return;
    setBulkSubmitting(estado);
    try {
      const observacion = bulkReview.observacion.trim() || undefined;
      await Promise.all([
        ...bulkReview.items.requisitos.map((evalId) => onRequisitoVeredicto?.(evalId, estado, observacion)),
        ...bulkReview.items.camposTerna.map(({ evalTernaId, campo }) => onTernaFieldVeredicto?.(evalTernaId, campo, estado, observacion)),
      ]);
      onRefresh?.();
      setBulkReview(null);
    } finally {
      setBulkSubmitting(null);
    }
  };

  const gridTemplate = useMemo(() => {
    const base = `6px ${columnas.map(() => '220px').join(' ')} 300px`;
    return hasAnyComment ? `${base} 300px` : base;
  }, [columnas, hasAnyComment]);

  const pendingDescriptions = useMemo(() => {
    const CAMPO_LABEL: Record<'observaciones' | 'planMejora' | 'fechaImplementacion', string> = {
      observaciones: 'Observaciones',
      planMejora: 'Plan de mejora',
      fechaImplementacion: 'Fecha de implementación',
    };
    // Mismo tipoHist que usa `computePendingByEvalSpan` para leer el historial de cada campo.
    const TIPO_HIST: Record<'observaciones' | 'planMejora' | 'fechaImplementacion', string> = {
      observaciones: 'OBSERVACIONES',
      planMejora: 'PLANMEJORA',
      fechaImplementacion: 'FECHAIMPLEMENTACION',
    };
    // "Pendiente por revisar" agrupa estados con significado distinto (isTernaFieldPendiente):
    // sin veredicto todavía (PENDIENTE/sin historial) vs. ya rechazado por el admin (esperando
    // que el asesor lo corrija) vs. comentado/con respuesta del asesor. Mostrar "pendiente" para
    // los tres por igual es engañoso — un RECHAZADO no está esperando al admin, está esperando
    // al asesor.
    const ESTADO_TEXTO: Record<string, string> = {
      RECHAZADO: 'rechazado, esperando corrección del asesor',
      COMENTADO: 'comentado, sin resolver',
      RESPUESTA_ASESOR: 'con respuesta del asesor, sin revisar',
    };

    const map = new Map<string, string>();
    pendingByEvalSpanGlobal.forEach((p, i) => {
      for (const evalId of p.requisitos) {
        for (const orden of ordenes) {
          for (const s of globalSpans[orden] ?? []) {
            if (s.req && calMap.get(s.req.requisitoId)?.evaluacionRequisito?.id === evalId && s.req.numeral) {
              const label = s.req.capitulo ? `${s.req.numeral}. ${s.req.capitulo}` : s.req.numeral;
              map.set(s.req.numeral, label);
            }
          }
        }
      }

      // Bug: un campo de terna pendiente (Observaciones/Plan de mejora/Fecha de implementación)
      // sumaba a `totalPendientes` pero nunca aparecía en esta lista — solo se recorría
      // `p.requisitos`, nunca `p.camposTerna`. Se identifica la fila con los numerales de los
      // requisitos que comparten esa fila (contexto), ya que la terna en sí no tiene numeral propio.
      if (p.camposTerna.length > 0) {
        const item = globalEvalSpans[i];
        const rowEnd = item ? item.startRow + item.span : null;
        const numerales: string[] = [];
        if (item) {
          for (const orden of ordenes) {
            for (const s of globalSpans[orden] ?? []) {
              if (s.req?.numeral && s.startRow < rowEnd! && s.startRow + s.span > item.startRow) {
                numerales.push(s.req.numeral);
              }
            }
          }
        }
        for (const { campo, evalTernaId } of p.camposTerna) {
          const historialEstado = latestHistorialMap.get(historialKey(TIPO_HIST[campo], evalTernaId))?.estado;
          const estadoTexto = historialEstado ? (ESTADO_TEXTO[historialEstado] ?? 'sin revisar') : 'sin revisar';
          const contexto = numerales.length > 0 ? ` (fila ${numerales.join(' / ')})` : '';
          map.set(`terna-${i}-${campo}`, `${CAMPO_LABEL[campo]} — ${estadoTexto}${contexto}`);
        }
      }
    });
    return Array.from(map.values());
  }, [pendingByEvalSpanGlobal, globalSpans, globalEvalSpans, ordenes, calMap, latestHistorialMap]);

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {isRevisionMode && totalPendientes > 0 && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200/90 rounded-lg px-4 py-2.5 text-[12px] text-amber-900 shadow-2xs">
          <div className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <div>
              <span>
                Advertencia: Hay {totalPendientes} campo(s) pendiente(s) por revisar en la matriz{' '}
                {totalPendientes > 0 ? ' en estos lugares:' : ''}
              </span>
              <ul className="flex flex-col max-h-48 overflow-y-auto text-[11px] font-normal mt-1 space-y-0.5 pr-2">
                {pendingDescriptions.map((element, idx) => {
                  return (
                    <li key={idx} className="whitespace-pre-line">
                      - {element}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
          <span className="text-[11px] font-bold text-amber-800 bg-amber-100 px-2.5 py-0.5 rounded-full border border-amber-300 shrink-0 self-start mt-0.5">
            {totalPendientes} sin veredicto
          </span>
        </div>
      )}

      {/* Search/Filter (Optional simplified) */}
      <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-2">
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-300 rounded-md text-[11px] font-bold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all active:scale-95 shadow-sm"
          >
            <RotateCw className="w-3 h-3 text-blue-600" />
            Actualizar matriz
          </button>
          {(hasDiffs || isRevisionMode || Boolean(historialRevision && historialRevision.length > 0)) && (
            <>
              <div className="h-4 w-px bg-gray-300" />
              <div className="flex bg-gray-100 p-0.5 rounded-lg border border-gray-200/60 shadow-inner">
                <button
                  type="button"
                  onClick={() => setMode('focus')}
                  className={`px-3 py-1 rounded-md text-[11px] font-bold transition-all ${
                    mode === 'focus' ? 'bg-blue-600 text-white shadow-xs' : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  Solo cambios
                </button>
                <button
                  type="button"
                  onClick={() => setMode('normal')}
                  className={`px-3 py-1 rounded-md text-[11px] font-bold transition-all ${
                    mode === 'normal' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  Ver todo
                </button>
              </div>
            </>
          )}
          <div className="h-4 w-px bg-gray-300" />
          <button
            type="button"
            onClick={() => setExpandAllTexts((v) => !v)}
            className={`px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${
              expandAllTexts
                ? 'bg-gray-800 text-white border-gray-800'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:text-gray-800'
            }`}
          >
            {expandAllTexts ? 'Colapsar textos' : 'Expandir textos'}
          </button>
          {hasAnyTextDiff && (
            <button
              type="button"
              onClick={() => setWordDiffAll((v) => !v)}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${
                wordDiffAll
                  ? 'bg-violet-600 text-white border-violet-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300 hover:text-violet-700'
              }`}
            >
              <GitCompare className="w-3.5 h-3.5" />
              {wordDiffAll ? 'Ocultar diff' : 'Ver diff'}
            </button>
          )}
          {isRevisionMode && totalPendientes > 0 && (
            <>
              <div className="h-4 w-px bg-gray-300" />
              <button
                type="button"
                disabled={aprobandoTodo || aprobandoFila !== null}
                onClick={handleAprobarTodo}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold bg-[#eaf5ee] text-[#2d7a4f] border border-[#b7dfc7] hover:bg-[#dcf0e3] transition-colors disabled:opacity-50"
              >
                {aprobandoTodo ? 'Aprobando...' : `Aprobar todo (${totalPendientes})`}
              </button>
            </>
          )}
          <div className="h-4 w-px bg-gray-300" />
          <div className="text-[11px] text-gray-500 font-medium">
            Última actualización:{' '}
            <span className="text-gray-700">
              {lastUpdated.toLocaleString('es-CO', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
            </span>
          </div>
        </div>
      </div>

      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-xs">
        <div className="overflow-auto min-h-[70vh] max-h-[70vh]">
          <div className="flex items-center">
            <div style={{ display: 'grid', gridTemplateColumns: gridTemplate, flex: '0 0 auto' }}>
              {/* Headers */}
              <div className="contents sticky z-30" style={{ top: 'calc(var(--admin-offset, 0px) + var(--admin-top-bar-height, 53px))' }}>
                <div className="bg-gray-50 border-b border-r border-gray-200 sticky z-30 top-0" style={{ gridColumn: 1 }} />
                {columnas.map((col, colIdx) => (
                  <div
                    key={col.ntcId}
                    className="p-3 bg-gray-50 border-b border-r border-gray-200 text-[10px] font-bold uppercase text-gray-500 sticky z-30 top-0"
                    style={{ gridColumn: colIdx + 2 }}
                  >
                    {col.nombre}
                  </div>
                ))}
                <div
                  className={`p-3 bg-gray-50 border-b border-gray-200 text-[10px] font-bold uppercase text-gray-500 sticky z-30 top-0 ${
                    hasAnyComment ? 'border-r' : ''
                  }`}
                  style={{ gridColumn: columnas.length + 2 }}
                >
                  Evaluación
                </div>
                {hasAnyComment && (
                  <div
                    className="p-3 bg-gray-50 border-b border-gray-200 text-[10px] font-bold uppercase text-gray-500 sticky z-30 top-0"
                    style={{ gridColumn: columnas.length + 3 }}
                  >
                    Comentarios
                  </div>
                )}
              </div>

              {/* Grid Body */}
              {activeFlatFilas.length > 0 ? (
                <div className="contents" style={{ gridTemplateRows: `repeat(${activeFlatFilas.length}, auto)` }}>
                  {/* Row Change Indicator Layer */}
                  {activeFlatFilas.map((filaObj, rowIndex) => {
                    const fila = filaObj.fila;
                    const evalKey = fila.ternaId ? `terna:${fila.ternaId}` : fila.grupo ? `grupo:${fila.grupo}` : null;
                    const hasEvalDiff = evalKey ? diffTernaMap.has(evalKey) : false;

                    let hasReqDiff = false;
                    ordenes.forEach((orden) => {
                      const item = activeSpans[orden].find((s) => rowIndex >= s.startRow && rowIndex < s.startRow + s.span);
                      if (item?.req && diffReqMap.has(item.req.requisitoId)) hasReqDiff = true;
                    });

                    const isLastInGrupo = activeFlatFilas[rowIndex]?.isLastInGrupo;

                    if (hasEvalDiff || hasReqDiff) {
                      return (
                        <div
                          key={`row-ind-${rowIndex}`}
                          className="z-20 relative"
                          style={{
                            gridColumn: 1,
                            gridRow: rowIndex + 2,
                            background: 'var(--admin-blue)',
                            borderRight: '1px solid #f3f4f6',
                            borderBottom: isLastInGrupo ? '2px solid #e5e7eb' : '1px solid #f3f4f6',
                          }}
                          title="Esta fila tiene cambios activos"
                        />
                      );
                    }

                    return (
                      <div
                        key={`row-ind-${rowIndex}`}
                        className="z-10"
                        style={{
                          gridColumn: 1,
                          gridRow: rowIndex + 2,
                          background: '#fff',
                          borderRight: '1px solid #f3f4f6',
                          borderBottom: isLastInGrupo ? '2px solid #e5e7eb' : '1px solid #f3f4f6',
                        }}
                      />
                    );
                  })}
                  {/* Each NTC Column */}
                  {ordenes.map((orden, colIdx) =>
                    activeSpans[orden].map((item, i) => {
                      const diff = item.req ? diffReqMap.get(item.req.requisitoId) : null;
                      const calObj = item.req ? calMap.get(item.req.requisitoId) : null;
                      const cal = diff ? diff.activa : (calObj?.calificacion ?? null);
                      const prev = diff ? (diff.prev ?? calObj?.calificacion ?? null) : null;

                      return (
                        <div
                          key={`col-${orden}-${i}`}
                          className={`p-3 border-r border-gray-100 bg-white ${diff ? 'bg-blue-50/20 ring-1 ring-blue-100 ring-inset z-10' : ''}`}
                          style={{
                            gridColumn: colIdx + 2,
                            gridRow: `${item.startRow + 2} / span ${item.span}`,
                            borderBottom: activeFlatFilas[item.startRow + item.span - 1]?.isLastInGrupo
                              ? '2px solid #e5e7eb'
                              : '1px solid #f3f4f6',
                          }}
                        >
                          {item.req ? (
                            (() => {
                              const evalReq = calMap.get(item.req.requisitoId)?.evaluacionRequisito;
                              const evalId = evalReq?.id;
                              const estadoRevision = evalReq?.estadoRevision;
                              const observacionRevision = evalReq?.observacion;
                              const tieneEvaluacionRequisito =
                                diffReqMap && diffReqMap.size > 0
                                  ? diffReqMap.has(item.req.requisitoId) || estadoRevision === 'RECHAZADO' || estadoRevision === 'APROBADO'
                                  : evalReq != null;
                              const isReqPendiente = !estadoRevision || estadoRevision === 'PENDIENTE';

                              return (
                                <RequisitoCell
                                  req={item.req}
                                  cal={cal}
                                  prev={prev}
                                  estadoRevision={estadoRevision}
                                  observacionRevision={observacionRevision}
                                  tieneEvaluacionRequisito={tieneEvaluacionRequisito}
                                  isRevisionMode={isRevisionMode}
                                  onOpenReview={
                                    isRevisionMode && evalId
                                      ? () =>
                                          openRequisitoReview(
                                            evalId,
                                            item.req!.numeral,
                                            item.req!.capitulo,
                                            item.req!.descripcion,
                                            cal,
                                            prev
                                          )
                                      : undefined
                                  }
                                  onBulkReview={
                                    isRevisionMode && item.span > 1
                                      ? () => setBulkReview({ items: pendingForRowRange(item.startRow, item.span), observacion: '' })
                                      : undefined
                                  }
                                  shiftHeld={shiftHeld}
                                  onQuickAprobar={
                                    isRevisionMode && evalId && onRequisitoVeredicto
                                      ? () => onRequisitoVeredicto(evalId, 'APROBADO')
                                      : undefined
                                  }
                                  onQuickRechazar={
                                    isRevisionMode && evalId && onRequisitoVeredicto
                                      ? () => onRequisitoVeredicto(evalId, 'RECHAZADO')
                                      : undefined
                                  }
                                />
                              );
                            })()
                          ) : (
                            <span className="text-gray-300 italic text-[11px]">—</span>
                          )}
                        </div>
                      );
                    })
                  )}

                  {/* Evaluation Column */}
                  {activeEvalSpans.map((item, i) => {
                    const diffTerna = item.evalKey ? diffTernaMap.get(item.evalKey) : null;
                    const evalTerna = item.evalKey ? evalTernaMap.get(item.evalKey) : null;

                    const activaFechaStr = getActivaFecha(diffTerna?.fechaImplementacion);
                    const anteriorFechaStr = getAnteriorFecha(diffTerna?.fechaImplementacion);
                    const isDateChanged = diffTerna?.fechaImplementacion !== undefined;

                    return (
                      <div
                        key={`eval-${i}`}
                        className={`p-3 bg-white ${hasAnyComment ? 'border-r border-gray-100' : ''} ${diffTerna ? 'bg-blue-50/20 ring-1 ring-blue-100 ring-inset z-10' : ''}`}
                        style={{
                          gridColumn: columnas.length + 2,
                          gridRow: `${item.startRow + 2} / span ${item.span}`,
                          borderBottom: activeFlatFilas[item.startRow + item.span - 1]?.isLastInGrupo
                            ? '2px solid #e5e7eb'
                            : '1px solid #f3f4f6',
                        }}
                      >
                        {isRevisionMode &&
                          (pendingByEvalSpan[i]?.requisitos.length ?? 0) + (pendingByEvalSpan[i]?.camposTerna.length ?? 0) > 0 && (
                            <button
                              type="button"
                              disabled={aprobandoTodo || aprobandoFila !== null}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAprobarFila(i, pendingByEvalSpan[i]!);
                              }}
                              className="mb-2 flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-[#eaf5ee] text-[#2d7a4f] border border-[#b7dfc7] hover:bg-[#dcf0e3] transition-colors disabled:opacity-50"
                            >
                              {aprobandoFila === i ? 'Aprobando...' : 'Aprobar fila'}
                            </button>
                          )}
                        {isRevisionMode && evalTerna ? (
                          <div className="space-y-2 mt-1">
                            <TernaFieldRevision
                              evalTernaId={evalTerna.id}
                              campo="observaciones"
                              label="Observaciones"
                              onVeredicto={onTernaFieldVeredicto}
                              value={
                                diffTerna?.observaciones?.activa !== undefined ? diffTerna.observaciones.activa : evalTerna.observaciones
                              }
                              prevValue={diffTerna?.observaciones?.activa !== undefined ? (diffTerna.observaciones.anterior ?? null) : null}
                              inDiffContext={diffTerna?.observaciones?.activa !== undefined}
                              forceExpand={expandAllTexts}
                              forceWordDiff={wordDiffAll}
                              onOpenReview={() =>
                                openTernaReview(
                                  evalTerna.id,
                                  'observaciones',
                                  'Observaciones',
                                  'OBSERVACIONES',
                                  diffTerna?.observaciones?.activa !== undefined ? diffTerna.observaciones.activa : evalTerna.observaciones,
                                  diffTerna?.observaciones?.activa !== undefined ? (diffTerna.observaciones.anterior ?? null) : null
                                )
                              }
                              latestHistorial={latestHistorialMap.get(historialKey('OBSERVACIONES', evalTerna.id))}
                            />
                            <TernaFieldRevision
                              evalTernaId={evalTerna.id}
                              campo="planMejora"
                              label="Plan de Mejora"
                              onVeredicto={onTernaFieldVeredicto}
                              value={diffTerna?.planMejora?.activa !== undefined ? diffTerna.planMejora.activa : evalTerna.planMejora}
                              prevValue={diffTerna?.planMejora?.activa !== undefined ? (diffTerna.planMejora.anterior ?? null) : null}
                              inDiffContext={diffTerna?.planMejora?.activa !== undefined}
                              forceExpand={expandAllTexts}
                              forceWordDiff={wordDiffAll}
                              onOpenReview={() =>
                                openTernaReview(
                                  evalTerna.id,
                                  'planMejora',
                                  'Plan de Mejora',
                                  'PLANMEJORA',
                                  diffTerna?.planMejora?.activa !== undefined ? diffTerna.planMejora.activa : evalTerna.planMejora,
                                  diffTerna?.planMejora?.activa !== undefined ? (diffTerna.planMejora.anterior ?? null) : null
                                )
                              }
                              latestHistorial={latestHistorialMap.get(historialKey('PLANMEJORA', evalTerna.id))}
                            />
                            <TernaFieldRevision
                              evalTernaId={evalTerna.id}
                              campo="fechaImplementacion"
                              label="Fecha Implementación"
                              onVeredicto={onTernaFieldVeredicto}
                              value={
                                activaFechaStr
                                  ? parseDateStr(activaFechaStr)
                                  : evalTerna.fechaImplementacion
                                    ? parseDateStr(evalTerna.fechaImplementacion)
                                    : null
                              }
                              prevValue={
                                anteriorFechaStr
                                  ? parseDateStr(anteriorFechaStr)
                                  : activaFechaStr && evalTerna.fechaImplementacion
                                    ? parseDateStr(evalTerna.fechaImplementacion)
                                    : null
                              }
                              inDiffContext={isDateChanged}
                              forceExpand={expandAllTexts}
                              forceWordDiff={wordDiffAll}
                              onOpenReview={() =>
                                openTernaReview(
                                  evalTerna.id,
                                  'fechaImplementacion',
                                  'Fecha Implementación',
                                  'FECHAIMPLEMENTACION',
                                  activaFechaStr
                                    ? parseDateStr(activaFechaStr)
                                    : evalTerna.fechaImplementacion
                                      ? parseDateStr(evalTerna.fechaImplementacion)
                                      : null,
                                  anteriorFechaStr
                                    ? parseDateStr(anteriorFechaStr)
                                    : activaFechaStr && evalTerna.fechaImplementacion
                                      ? parseDateStr(evalTerna.fechaImplementacion)
                                      : null
                                )
                              }
                              latestHistorial={latestHistorialMap.get(historialKey('FECHAIMPLEMENTACION', evalTerna.id))}
                            />
                          </div>
                        ) : (
                          <>
                            <DiffText
                              label="Observaciones"
                              inDiffContext={diffTerna?.observaciones?.activa !== undefined}
                              current={
                                diffTerna?.observaciones?.activa !== undefined ? diffTerna.observaciones.activa : evalTerna?.observaciones
                              }
                              prev={diffTerna?.observaciones?.activa !== undefined ? (diffTerna.observaciones.anterior ?? null) : null}
                              wordDiff={wordDiffAll}
                            />
                            <DiffText
                              label="Plan de Mejora"
                              inDiffContext={diffTerna?.planMejora?.activa !== undefined}
                              current={diffTerna?.planMejora?.activa !== undefined ? diffTerna.planMejora.activa : evalTerna?.planMejora}
                              prev={diffTerna?.planMejora?.activa !== undefined ? (diffTerna.planMejora.anterior ?? null) : null}
                              wordDiff={wordDiffAll}
                            />
                            <DiffText
                              label="Fecha Implementación"
                              inDiffContext={isDateChanged}
                              current={
                                activaFechaStr
                                  ? parseDateStr(activaFechaStr)
                                  : evalTerna?.fechaImplementacion
                                    ? parseDateStr(evalTerna.fechaImplementacion)
                                    : null
                              }
                              prev={anteriorFechaStr ? parseDateStr(anteriorFechaStr) : null}
                              wordDiff={false}
                            />
                          </>
                        )}

                        {/* Linked Evidences Diff */}
                        {(evalTerna?.evidenciaIds?.length || !!diffTerna?.evidenciaIds) && (
                          <div className="mt-3 pt-2 border-t border-gray-100">
                            <div className="text-[9px] font-bold uppercase text-gray-400 mb-1.5 flex items-center justify-between">
                              <span>Evidencias</span>
                              {diffTerna?.evidenciaIds && (
                                <span className="text-[8px] bg-blue-100 text-blue-600 px-1 rounded">Editado</span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {(() => {
                                const savedIds = evalTerna?.evidenciaIds || [];
                                // evidenciaIds can be number[] (old format) or {activa, anterior} (new format)
                                const rawDiff = diffTerna?.evidenciaIds;
                                const activeIds: number[] = !rawDiff
                                  ? savedIds
                                  : Array.isArray(rawDiff)
                                    ? rawDiff
                                    : (rawDiff.activa ?? savedIds);
                                const prevIds: number[] = !rawDiff
                                  ? savedIds
                                  : Array.isArray(rawDiff)
                                    ? savedIds
                                    : (rawDiff.anterior ?? savedIds);

                                const allIdsSet = new Set([...prevIds, ...activeIds]);

                                return Array.from(allIdsSet).map((id) => {
                                  const ev = data.evidencias.find((e) => e.evidenciaId === id);
                                  if (!ev) return null;

                                  const isRemoved = prevIds.includes(id) && !activeIds.includes(id);
                                  const isNew = !prevIds.includes(id) && activeIds.includes(id);

                                  return (
                                    <a
                                      key={ev.evidenciaId}
                                      href={ev.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`
                                      text-[9px] px-2 py-0.5 rounded transition-all flex items-center gap-1.5 border
                                      ${isRemoved ? 'opacity-30 grayscale line-through bg-gray-50 border-gray-200 text-gray-400' : ''}
                                      ${isNew ? 'bg-blue-50 border-blue-300 text-blue-700 font-bold ring-1 ring-blue-200' : ''}
                                      ${!isRemoved && !isNew ? 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100' : ''}
                                    `}
                                    >
                                      {isNew && <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />}
                                      <span className="wrap-break-word max-w-30">{ev.nombre}</span>
                                      {isNew && <span className="text-[7px] bg-blue-600 text-white px-0.5 rounded ml-0.5">NUEVA</span>}
                                    </a>
                                  );
                                });
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Comentarios Column (only when there is at least one saved comment) */}
                  {hasAnyComment &&
                    activeEvalSpans.map((item, i) => {
                      const groups = commentsByEvalSpan[i] ?? [];
                      return (
                        <div
                          key={`comentarios-${i}`}
                          className="p-3 bg-white"
                          style={{
                            gridColumn: columnas.length + 3,
                            gridRow: `${item.startRow + 2} / span ${item.span}`,
                            borderBottom: activeFlatFilas[item.startRow + item.span - 1]?.isLastInGrupo
                              ? '2px solid #e5e7eb'
                              : '1px solid #f3f4f6',
                          }}
                        >
                          {groups.length > 0 && (
                            <div className="space-y-2">
                              {groups.map((g) => (
                                <SavedCommentGroup key={g.key} label={g.label} subtitle={g.subtitle} appeal={g.appeal} admin={g.admin} />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="col-span-full p-12 text-center text-gray-400 text-sm">
                  {mode === 'focus' ? 'No hay cambios registrados en esta asesoría.' : 'No hay requisitos cargados.'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {reviewModal && (
        <RevisionFieldModal
          open={reviewModal !== null}
          onClose={() => setReviewModal(null)}
          titulo={reviewModal.titulo}
          kind={reviewModal.kind}
          textoActual={reviewModal.textoActual}
          textoAnterior={reviewModal.textoAnterior}
          calActual={reviewModal.calActual}
          calAnterior={reviewModal.calAnterior}
          numeral={reviewModal.numeral}
          capitulo={reviewModal.capitulo}
          historialList={reviewModal.historialList}
          estadoActual={reviewModal.estadoActual}
          observacionActual={reviewModal.observacionActual}
          onAprobar={reviewModal.onAprobar}
          onRechazar={reviewModal.onRechazar}
        />
      )}

      {bulkReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setBulkReview(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-gray-900 mb-1">Revisar grupo en bloque</h3>
            <p className="text-xs text-gray-500 mb-3">
              {bulkReview.items.requisitos.length + bulkReview.items.camposTerna.length > 0
                ? `Se aplicará el mismo veredicto y mensaje a ${bulkReview.items.requisitos.length + bulkReview.items.camposTerna.length} elemento(s) pendiente(s) de este grupo.`
                : 'No hay elementos pendientes de revisión en este grupo.'}
            </p>
            <textarea
              rows={3}
              value={bulkReview.observacion}
              onChange={(e) => setBulkReview((prev) => (prev ? { ...prev, observacion: e.target.value } : prev))}
              placeholder="Mensaje para el historial (opcional)..."
              className="w-full px-2.5 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300 resize-none mb-3"
            />
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setBulkReview(null)}
                className="px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={!!bulkSubmitting || bulkReview.items.requisitos.length + bulkReview.items.camposTerna.length === 0}
                onClick={() => handleBulkVeredicto('RECHAZADO')}
                className="px-3 py-1.5 text-xs font-bold rounded-md border border-red-200 bg-white text-red-700 hover:bg-red-50 disabled:opacity-40 transition-colors"
              >
                {bulkSubmitting === 'RECHAZADO' ? 'Rechazando...' : 'Rechazar todo'}
              </button>
              <button
                type="button"
                disabled={!!bulkSubmitting || bulkReview.items.requisitos.length + bulkReview.items.camposTerna.length === 0}
                onClick={() => handleBulkVeredicto('APROBADO')}
                className="px-3 py-1.5 text-xs font-bold rounded-md border border-green-200 bg-[#eaf5ee] text-[#2d7a4f] hover:bg-[#dcf0e3] disabled:opacity-40 transition-colors"
              >
                {bulkSubmitting === 'APROBADO' ? 'Aprobando...' : 'Aprobar todo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function MatrizSkeleton() {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-xs animate-pulse">
      <div className="flex flex-col h-[70vh]">
        {/* Header Skeleton */}
        <div className="grid grid-cols-4 bg-gray-50 border-b border-gray-200">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="p-4 border-r border-gray-200 last:border-r-0">
              <div className="h-2 w-24 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
        {/* Body Skeleton */}
        <div className="flex-1 overflow-hidden p-4 space-y-6">
          {[1, 2, 3, 4].map((row) => (
            <div key={row} className="grid grid-cols-4 gap-4">
              {[1, 2, 3].map((col) => (
                <div key={col} className="space-y-2">
                  <div className="h-3 w-8 bg-gray-100 rounded" />
                  <div className="h-2 w-full bg-gray-50 rounded" />
                  <div className="h-2 w-2/3 bg-gray-50 rounded" />
                </div>
              ))}
              <div className="space-y-2 bg-gray-50/50 p-2 rounded">
                <div className="h-2 w-full bg-gray-200 rounded" />
                <div className="h-2 w-3/4 bg-gray-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
